<script lang="ts">
  import ArrowDown from '@lucide/svelte/icons/arrow-down';
  import ArrowUp from '@lucide/svelte/icons/arrow-up';
  import Plus from '@lucide/svelte/icons/plus';
  import Trash2 from '@lucide/svelte/icons/trash-2';
  import TriangleAlert from '@lucide/svelte/icons/triangle-alert';

  import { getServices } from '../../app/context';
  import { createDefaultRatings } from '../../model/defaults';
  import { replaceRatings, setRatings } from '../../model/ops';
  import { normalizeColor, normalizeName } from '../../model/text';
  import type { Rating } from '../../model/types';
  import { LIMITS } from '../../model/types';
  import { format, strings, visionNames } from '../../strings';
  import Button from '../../ui/Button.svelte';
  import { confusablePairs } from '../../ui/color';
  import Dialog from '../../ui/Dialog.svelte';
  import IconButton from '../../ui/IconButton.svelte';

  let { open = $bindable(false) }: { open?: boolean } = $props();

  const { store, toasts } = getServices();
  const id = $props.id();
  let draft: Rating[] = $state([]);
  /** Raw text of each hex field, so half-typed values aren't overwritten. */
  let hexText: Record<string, string> = $state({});

  function load(ratings: readonly Rating[]) {
    draft = ratings.map((rating) => ({ ...rating }));
    hexText = Object.fromEntries(ratings.map((rating) => [rating.id, rating.color]));
  }

  $effect(() => {
    if (open) load(store.doc.ratings);
  });

  const valid = $derived(
    draft.length >= LIMITS.minRatings &&
      draft.every((r) => normalizeName(r.name) !== null && normalizeColor(r.color) !== null),
  );
  const warnings = $derived(
    confusablePairs(draft.map((r) => normalizeColor(r.color) ?? '#000000')).map(
      ({ a, b, vision }) =>
        format.confusable(draft[a]?.name ?? '', draft[b]?.name ?? '', visionNames[vision]),
    ),
  );

  function move(i: number, j: number) {
    const next = [...draft];
    const [a, b] = [next[i], next[j]];
    if (!a || !b) return;
    next[i] = b;
    next[j] = a;
    draft = next;
  }

  function setColor(rating: Rating, value: string) {
    hexText[rating.id] = value;
    const color = normalizeColor(value);
    if (color) rating.color = color;
  }

  function save() {
    if (!valid) return;
    const ratings = $state.snapshot(draft);
    store.apply((doc) => setRatings(doc, ratings));
    open = false;
  }

  function resetToDefaults() {
    store.apply((doc) => replaceRatings(doc, createDefaultRatings(store.newId)));
    load(store.doc.ratings);
    toasts.show(strings.ratingsReset, { action: { label: strings.undo, run: () => store.undo() } });
  }
</script>

<Dialog bind:open title={strings.ratingsTitle} description={strings.ratingsDescription} size="lg">
  <form
    id="{id}-form"
    class="flex flex-col gap-3"
    onsubmit={(event) => {
      event.preventDefault();
      save();
    }}
  >
    <ol class="flex flex-col gap-2">
      {#each draft as rating, i (rating.id)}
        {@const color = normalizeColor(rating.color) ?? '#000000'}
        <li class="flex flex-wrap items-center gap-2 sm:flex-nowrap">
          <input
            type="color"
            value={color.toLowerCase()}
            aria-label="{strings.ratingColor}: {rating.name}"
            class="size-10 shrink-0 cursor-pointer rounded-lg border border-line-strong/70 bg-surface p-1"
            oninput={(event) => {
              setColor(rating, event.currentTarget.value);
            }}
          />
          <input
            value={hexText[rating.id] ?? color}
            aria-label="{strings.ratingColor} (hex): {rating.name}"
            aria-invalid={normalizeColor(hexText[rating.id] ?? color) === null}
            spellcheck="false"
            maxlength={7}
            class="min-h-10 w-24 rounded-lg border border-line-strong/70 bg-surface px-2 font-mono text-base text-ink uppercase aria-invalid:border-danger sm:text-sm"
            oninput={(event) => {
              setColor(rating, event.currentTarget.value);
            }}
          />
          <input
            bind:value={rating.name}
            aria-label="{strings.ratingName} {i + 1}"
            required
            maxlength={LIMITS.name}
            class="min-h-10 min-w-0 flex-1 rounded-lg border border-line-strong/70 bg-surface px-3 text-base text-ink sm:text-sm"
          />
          <div class="flex">
            <IconButton
              size="sm"
              icon={ArrowUp}
              label={format.moveUp(rating.name)}
              disabled={i <= 1}
              onclick={() => {
                move(i, i - 1);
              }}
            />
            <IconButton
              size="sm"
              icon={ArrowDown}
              label={format.moveDown(rating.name)}
              disabled={i === 0 || i === draft.length - 1}
              onclick={() => {
                move(i, i + 1);
              }}
            />
            <IconButton
              size="sm"
              icon={Trash2}
              label={format.remove(rating.name)}
              disabled={i === 0 || draft.length <= LIMITS.minRatings}
              onclick={() => {
                draft = draft.filter((r) => r.id !== rating.id);
              }}
            />
          </div>
        </li>
      {/each}
    </ol>
    <div class="flex flex-wrap gap-2">
      <Button
        size="sm"
        variant="ghost"
        onclick={() => {
          const rating = { id: store.newId(), name: strings.newRatingName, color: '#808080' };
          draft = [...draft, rating];
          hexText[rating.id] = rating.color;
        }}
      >
        <Plus size={16} aria-hidden="true" />
        {strings.addRating}
      </Button>
      <Button size="sm" variant="ghost" onclick={resetToDefaults}>{strings.resetRatings}</Button>
    </div>
    <p class="text-xs text-ink-muted">{strings.colorblindNote}</p>
    {#if warnings.length > 0}
      <ul class="flex flex-col gap-1 rounded-lg bg-danger-soft p-3 text-sm text-ink" role="status">
        {#each warnings as warning (warning)}
          <li class="flex items-start gap-2">
            <TriangleAlert size={16} aria-hidden="true" class="mt-0.5 shrink-0 text-danger" />
            {warning}
          </li>
        {/each}
      </ul>
    {/if}
  </form>
  {#snippet footer()}
    <Button onclick={() => (open = false)}>{strings.cancel}</Button>
    <Button variant="primary" type="submit" form="{id}-form" disabled={!valid}>
      {strings.save}
    </Button>
  {/snippet}
</Dialog>
